import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { SignOut, Envelope } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import ProfileForm from "../components/account/ProfileForm";
import PartnerSearch from "../components/account/PartnerSearch";
import LocationSettingsCard from "../components/account/LocationSettingsCard";
import { accountService } from "../services/accountService";
import { authService } from "../services/authService";
import { useAuthStore } from "../stores/authStore";
import { useProfileCatalogs } from "../hooks/useProfileCatalogs";

export default function AccountPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const userId = user?.id;

  const profileQuery = useQuery({
    queryKey: ["user", "profile", userId],
    queryFn: () => accountService.getProfile(),
    enabled: userId != null,
  });

  const catalogs = useProfileCatalogs();

  const updateMutation = useMutation({
    mutationFn: (patch) => accountService.updateProfile(patch),
    onSuccess: (updated) => {
      queryClient.setQueryData(["user", "profile", userId], updated);
      toast.success("Perfil actualizado.");
    },
    onError: (err) => {
      toast.error(err?.message || "No se pudo actualizar");
    },
  });

  const resendVerifyMutation = useMutation({
    mutationFn: () => authService.resendVerificationEmail(),
    onSuccess: () => {
      toast.success("Si hace falta, revisa el correo (también spam).");
    },
    onError: (err) => {
      toast.error(err?.message || "No se pudo reenviar el correo");
    },
  });

  async function save(patch) {
    await updateMutation.mutateAsync(patch);
  }

  async function linkPartner(partner) {
    try {
      const updated = await accountService.updateProfile({
        hasPartner: true,
        partnerRegistered: true,
        partnerId: partner.id,
        partnerNick: partner.nick,
      });
      queryClient.setQueryData(["user", "profile", userId], updated);
      toast.success(`Vinculado a ${partner.nick}.`);
    } catch (err) {
      toast.error(err?.message || "No se pudo vincular");
    }
  }

  async function unlinkPartner() {
    try {
      const updated = await accountService.updateProfile({
        partnerId: null,
        partnerNick: null,
        partnerRegistered: false,
      });
      queryClient.setQueryData(["user", "profile", userId], updated);
      toast.success("Pareja desvinculada.");
    } catch (err) {
      toast.error(err?.message || "Error");
    }
  }

  async function setHasPartner(value) {
    try {
      const patch = { hasPartner: value };
      if (!value) {
        patch.partnerRegistered = false;
        patch.partnerId = null;
        patch.partnerNick = null;
      }
      const updated = await accountService.updateProfile(patch);
      queryClient.setQueryData(["user", "profile", userId], updated);
    } catch (err) {
      toast.error(err?.message || "Error");
    }
  }

  async function setPartnerRegistered(value) {
    try {
      const patch = { partnerRegistered: value };
      if (!value) {
        patch.partnerId = null;
        patch.partnerNick = null;
      }
      const updated = await accountService.updateProfile(patch);
      queryClient.setQueryData(["user", "profile", userId], updated);
    } catch (err) {
      toast.error(err?.message || "Error");
    }
  }

  async function logout() {
    await authService.logout();
    queryClient.clear();
    navigate("/auth", { replace: true });
  }

  if (profileQuery.isLoading || !profileQuery.data) {
    return (
      <div className="px-5 pt-6 text-xs uppercase tracking-[0.22em] text-[rgba(255,255,255,0.45)]">
        {profileQuery.isError ? profileQuery.error?.message || "Error al cargar" : "Cargando..."}
      </div>
    );
  }

  const profile = profileQuery.data;

  return (
    <div className="px-5 pt-6 pb-10" data-testid="account-page">
      <div className="mx-auto max-w-3xl">
        <header className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="zx-label">Tu identidad</p>
            <h1 className="font-serif text-3xl tracking-tight md:text-4xl">
              {profile.nick || user?.nick || "Cuenta"}
            </h1>
            <p className="mt-1 text-sm text-[rgba(255,255,255,0.6)]">{user?.email}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => resendVerifyMutation.mutate()}
              disabled={resendVerifyMutation.isPending}
              className="flex items-center gap-2 rounded-full border border-[rgba(255,255,255,0.12)] px-3 py-2 text-[10px] uppercase tracking-[0.22em] text-[rgba(255,255,255,0.65)] hover:border-[rgba(255,255,255,0.3)]"
              data-testid="resend-verification-button"
            >
              <Envelope size={12} weight="thin" /> Verificar email
            </button>
            <button
              onClick={logout}
              className="flex items-center gap-2 rounded-full border border-[rgba(255,255,255,0.12)] px-3 py-2 text-[10px] uppercase tracking-[0.22em] text-[rgba(255,255,255,0.65)] hover:border-[rgba(255,255,255,0.3)]"
              data-testid="logout-button"
            >
              <SignOut size={12} weight="thin" /> Salir
            </button>
          </div>
        </header>

        <div className="space-y-6 zx-stagger">
          <LocationSettingsCard />

          <ProfileForm profile={profile} onSave={save} catalogs={catalogs} />

          <div className="zx-card p-5 space-y-5">
            <p className="zx-label">Pareja</p>
            <ToggleRow
              label="¿Tienes pareja?"
              value={profile.hasPartner}
              onChange={setHasPartner}
              testId="toggle-has-partner"
            />
            {profile.hasPartner && (
              <>
                <ToggleRow
                  label="¿Está registrada en ZyrisX?"
                  value={profile.partnerRegistered}
                  onChange={setPartnerRegistered}
                  testId="toggle-partner-registered"
                />
                {profile.partnerRegistered && (
                  <PartnerSearch
                    current={profile.partnerNick}
                    onLink={linkPartner}
                    onUnlink={unlinkPartner}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ToggleRow({ label, value, onChange, testId }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-[rgba(255,255,255,0.75)]">{label}</span>
      <button
        onClick={() => onChange(!value)}
        data-testid={testId}
        className={[
          "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
          value ? "bg-[#8B1E3F]" : "bg-[rgba(255,255,255,0.12)]",
        ].join(" ")}
        aria-pressed={value}
      >
        <span
          className={[
            "inline-block h-4 w-4 transform rounded-full bg-[#f5f5f5] transition-transform",
            value ? "translate-x-6" : "translate-x-1",
          ].join(" ")}
        />
      </button>
    </div>
  );
}
